import React, { ComponentProps } from 'react';
import {
  BlockInteractions,
  Views as BlockInteractionsViews,
  DISPLAY_TYPE,
  createButtonObject,
} from '../../components/organisms/BlockInteractions';
import { Namespace } from '../../translation/namespace';
import { Icons } from '../../components/atoms/SVG';
import { BlockType } from '../../types/api/ElementType';
import { asFetchableBlock, useBlockContext, useRefreshBlock } from '../../_lib/BlockFetcher';
import { DefaultView, EditView, Views } from '../../types/BlockWithViews';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { GenericSearchListEditView } from './views/GenericSearchListEditView';
import { GenericSearchListDefaultView } from './views/GenericSearchListDefaultView';
import { useContentWrapperContext } from '../../components/blocks/ContentWrapper/ContentWrapperContext';
import { useWidgetContext } from '../../_lib/components/Widget';
import { useContentWrapperDndContext } from '../../components/blocks/ContentWrapper/ContentWrapperDndProvider';
import { headlineHierarchy } from '../../components/organisms/BlockInteractions/BlockInteractions.utils';

type GenericSearchListProps = Views<DefaultView<any> & EditView<any>> & { widgetTitle: string };

/**
 * Renders a search result or a form to create a search result.
 * - inside contentwrapper we need to pass specific translation that contains position info
 */
function GenericSearchList({ views, activeView }: GenericSearchListProps) {
  const { t } = useSonTranslation('GenericSearchList');
  const { uuid, elementType } = useBlockContext();
  const { refresh, refreshCount } = useRefreshBlock();
  const contentWrapperContext = useContentWrapperContext();
  const contentWrapperDndContext = useContentWrapperDndContext();

  const { header } = useWidgetContext();

  const blockInteractionButtons = () => {
    const editButton = createButtonObject(
      BlockInteractionsViews.EDIT,
      Icons.EDIT_PENCIL,
      `${Namespace.GenericSearchList}:actions.${contentWrapperContext ? 'editInContentWrapper' : 'edit'}`,
      DISPLAY_TYPE.COLLAPSIBLE,
      <GenericSearchListEditView {...views.edit} />
    );

    const deleteButton = createButtonObject(
      BlockInteractionsViews.DELETE,
      Icons.ERASER,
      `${Namespace.GenericSearchList}:buttons.${contentWrapperContext ? 'deleteInContentWrapper' : 'delete'}`,
      DISPLAY_TYPE.MODAL,
      null
    );

    return { ...editButton, ...deleteButton };
  };

  return (
    <BlockInteractions
      contentWrapperContext={contentWrapperContext}
      contentWrapperDndContext={contentWrapperDndContext}
      headline={{
        text: t('headline'),
        // header is set, if it's used in a widget => we already have a parent h2, so we use h3 here
        hierarchy: headlineHierarchy(
          header ? 3 : 2,
          contentWrapperContext?.parentIndex,
          contentWrapperContext?.grandParentIndex
        ),
        visible: true,
      }}
      alwaysShowInfo={false}
      views={views}
      activeView={activeView}
      buttons={blockInteractionButtons()}
      refreshBlock={refresh}
      refreshCount={refreshCount}
      uuid={uuid}
      elementType={elementType}
    >
      <GenericSearchListDefaultView {...views.default} />
    </BlockInteractions>
  );
}

export const GenericSearchListBlock = asFetchableBlock(GenericSearchList, BlockType.genericsearchlist);
export type GenericSearchListBlock = ComponentProps<typeof GenericSearchListBlock>;
